import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '@/store' // eslint-disable-line
import {
  clearPendingMfaChallenge,
  getPendingMfaChallenge,
  isMfaChallengeExpired,
} from '@/utils/mfaChallenge'

Vue.use(VueRouter)

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
  routes: [
    {
      path: '/',
      name: 'login',
      component: () => import('@/views/Login.vue'),
      meta: {
        isPublic: true,
        layout: 'full',
      },
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('@/views/ForgotPassword.vue'),
      meta: {
        isPublic: true,
        layout: 'full',
      },
    },
    {
      path: '/password-reset',
      name: 'password-reset',
      component: () => import('@/views/PasswordReset.vue'),
      meta: {
        isPublic: true,
        layout: 'full',
      },
    },
    {
      path: '/mfa',
      name: 'mfa',
      component: () => import('@/views/MfaVerification.vue'),
      meta: {
        isPublic: true,
        layout: 'full',
      },
    },
    {
      path: '/activate-account',
      alias: '/api/:version/Auth/ActivateAccount',
      name: 'activate-account',
      component: () => import('@/views/ActivateAccount.vue'),
      meta: {
        isPublic: true,
        layout: 'full',
      },
    },
    {
      path: '/mfa-recovery',
      name: 'mfa-recovery',
      component: () => import('@/views/MfaRecoveryRequired.vue'),
      meta: {
        isPublic: true,
        layout: 'full',
      },
    },
    {
      path: '/planning',
      name: 'planning',
      component: () => import('@/views/calendar/Calendar.vue'),
      meta: {
        contentClass: 'planning',
      },
    },
    {
      path: '/jeunes',
      name: 'youngs',
      component: () => import('@/views/youngs/Youngs.vue'),
      meta: {
        contentRenderer: 'sidebar-left',
        contentClass: 'youngs-view',
      },
    },
    {
      path: '/repondeur',
      name: 'history',
      component: () => import('@/views/history/History.vue'),
      meta: {
        contentClass: 'history',
      },
    },
    {
      path: '/blocklist',
      name: 'blocklist',
      component: () => import('@/views/blocklist/Blocklist.vue'),
      meta: {
        contentClass: 'blocklist',
      },
    },
    {
      path: '/statistiques',
      name: 'statistics',
      component: () => import('@/views/statistics/Statistics.vue'),
      meta: {
        contentClass: 'statistics',
      },
    },
    {
      path: '/utilisateurs',
      name: 'utilisateurs',
      component: () => import('@/views/users/Users.vue'),
      meta: {
        contentClass: 'utilisateurs',
      },
    },
    {
      path: '/profile',
      name: 'profile',
      component: () => import('@/views/users/UserProfile.vue'),
      meta: {
        contentClass: 'profile',
      },
    },
    {
      path: '/mobile-settings',
      name: 'mobile-settings',
      component: () => import('@/views/settings/MobileSettings.vue'),
      meta: {
        contentClass: 'mobile-settings',
      },
    },
    {
      path: '/demandes-hebergement',
      name: 'accommodation-requests',
      component: () => import('@/views/accommodation/AccommodationRequests.vue'),
      meta: {
        contentClass: 'accommodation-requests',
      },
    },
    {
      path: '/error-401',
      name: 'error-401',
      component: () => import('@/views/Error.vue'),
      props: {
        error: 'Utilisateur non authentifié',
        errorCode: 401,
      },
      meta: {
        skipPermission: true,
        isPublic: true,
        layout: 'full',
      },
    },
    {
      path: '/error-404',
      name: 'error-404',
      component: () => import('@/views/Error.vue'),
      props: {
        error: 'Page non trouvée',
        errorCode: 404,
      },
      meta: {
        skipPermission: true,
        isPublic: true,
        layout: 'full',
      },
    },
    {
      path: '/home',
      redirect: 'planning',
    },
    {
      path: '*',
      redirect: 'error-404',
    },
  ],
})

// Exported so other app-wide concerns (e.g. real-time notifications) can reuse
// the same source of truth for "which roles can access which areas of the app".
export const userRolesAuthorizedPaths = {
  'LeRefuge.Administrateur': ['/utilisateurs', '/statistiques', '/profile', '/mobile-settings', '/'],
  'LeRefuge.Coordinateur': ['/planning', '/jeunes', '/repondeur', '/statistiques', '/utilisateurs', '/blocklist', '/profile', '/demandes-hebergement', '/'],
  'LeRefuge.Ecoutant': ['/planning', '/jeunes', '/repondeur', '/blocklist', '/profile', '/demandes-hebergement', '/'],
  'LeRefuge.SuperAdmin': ['/planning', '/jeunes', '/repondeur', '/statistiques', '/utilisateurs', '/blocklist', '/profile', '/mobile-settings', '/demandes-hebergement', '/'],
}

router.beforeEach(async (to, from, next) => {
  if (to.meta.isPublic) {
    if (to.name === 'mfa') {
      const challenge = getPendingMfaChallenge()
      if (!challenge || isMfaChallengeExpired(challenge)) {
        clearPendingMfaChallenge()
        next({ name: 'login' })
        return
      }
    }

    next()
    return
  }

  let userId = store.getters['auth/getUserId']

  if (!userId) {
    try {
      userId = await store.dispatch('auth/restoreSession')
    } catch (error) {
      await store.dispatch('auth/resetSession')
      next({ name: 'login' })
      return
    }
  }

  if (userId) {
    // Save user ID to Vuex store
    store.commit('auth/setUserId', userId)

    // Fetch user data
    // Check if user is already loaded to avoid redundant API calls
    const currentUser = store.getters['users/currentUser']
    const hasRole = store.getters['users/getUserRole']

    if (!currentUser || currentUser.id !== userId || !hasRole) {
      try {
        await store.dispatch('users/getUserAndSetStateById', userId)
      } catch (error) {
        console.error('Failed to fetch user data:', error)
        next({ name: 'login' })
        return
      }
    }

    // Permission check
    try {
      const userRole = store.getters['users/getUserRole']
      if (userRolesAuthorizedPaths[userRole]?.includes(to.path) || to.meta.skipPermission) {
        next()
      } else {
        next({ path: userRolesAuthorizedPaths[userRole]?.at(0) || '/login' })
      }
    } catch (error) {
      console.error('Error during permission check:', error)
      next({ name: 'login' })
    }
    return
  }

  next({ name: 'login' })
})

// ? For splash screen
// Remove afterEach hook if you are not using splash screen
router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg')
  if (appLoading) {
    appLoading.style.display = 'none'
  }
})

export default router
